import { Component, type ReactNode } from 'react';
import { AlertCircle, RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/button';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error?: Error;
}

/**
 * React error boundary that catches render errors and displays a fallback UI.
 * Use this to wrap sections of your app that might throw during render.
 */
export class ErrorBoundary extends Component<Props, State> {
  state: State = { hasError: false };

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo): void {
    console.error('Error boundary caught:', error, info);
  }

  handleRetry = (): void => {
    this.setState({ hasError: false, error: undefined });
  };

  render(): ReactNode {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      return (
        <div className="flex min-h-[200px] flex-col items-center justify-center p-8 text-center">
          <AlertCircle className="text-destructive mb-4 h-12 w-12" />
          <h2 className="text-destructive text-lg font-semibold">Something went wrong</h2>
          <p className="text-muted-foreground mt-2 max-w-md">
            An unexpected error occurred. Please try refreshing the page or contact support if the
            problem persists.
          </p>
          <Button variant="outline" onClick={this.handleRetry} className="mt-4">
            <RefreshCw className="mr-2 h-4 w-4" />
            Try again
          </Button>
        </div>
      );
    }

    return this.props.children;
  }
}

/**
 * Minimal inline error fallback for smaller UI sections.
 */
export function InlineErrorFallback({
  message = 'Failed to load',
  onRetry,
}: {
  message?: string;
  onRetry?: () => void;
}): ReactNode {
  return (
    <div className="flex items-center gap-2 p-2 text-sm">
      <AlertCircle className="text-destructive h-4 w-4" />
      <span className="text-muted-foreground">{message}</span>
      {onRetry && (
        <Button variant="ghost" size="sm" onClick={onRetry} className="h-6 px-2">
          Retry
        </Button>
      )}
    </div>
  );
}
